import { useState, type FormEvent } from 'react'
import { Banner, Button, Dialog, Field, Input } from '@/components/ui'
import { errorMessage } from '@/lib/api'
import { t } from '@/lib/i18n'
import { OtpInput } from '../components/OtpInput'
import { useMfaVerify } from './api'

export interface MfaVerifyDialogProps {
  open: boolean
  /** A step up (refunds, role changes, turning the factor off): the copy says so. */
  stepUp?: boolean
  /** Called after the code was accepted. */
  onVerified: () => void
  /** Called when the person closes the dialog without a code. */
  onCancel: () => void
}

/** G-03: "Enter the code from your Authenticator app", or a recovery code instead. */
export function MfaVerifyDialog(props: MfaVerifyDialogProps) {
  // The body remounts on every opening, so the fields start empty each time.
  return props.open ? <VerifyBody {...props} /> : null
}

function VerifyBody({ open, stepUp, onVerified, onCancel }: MfaVerifyDialogProps) {
  const [mode, setMode] = useState<'code' | 'recovery'>('code')
  const [code, setCode] = useState('')
  const [recovery, setRecovery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const verify = useMfaVerify()

  const submit = async (e?: FormEvent, value = code) => {
    e?.preventDefault()
    if (verify.isPending) return
    setError(null)
    try {
      await verify.mutateAsync(mode === 'code' ? { code: value } : { recoveryCode: recovery.trim() })
      onVerified()
    } catch (err) {
      setError(errorMessage(err))
      if (mode === 'code') setCode('')
    }
  }

  const switchMode = () => {
    setError(null)
    setMode(mode === 'code' ? 'recovery' : 'code')
  }

  const canSubmit = mode === 'code' ? code.length === 6 : recovery.trim().length >= 12
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
      size="sm"
      title={stepUp ? t('Confirm this change') : t('Two factor authentication')}
      description={
        mode === 'code'
          ? t('Open your Authenticator app and enter the 6 digit code for MG AI School.')
          : t('Enter one of the recovery codes you saved. Each code works once.')
      }
    >
      <form onSubmit={(e) => void submit(e)} noValidate className="space-y-4 pb-1" data-testid="mfa-verify">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {mode === 'code' ? (
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={(v) => void submit(undefined, v)}
            autoFocus
            invalid={!!error}
            label={t('Authenticator app code')}
          />
        ) : (
          <Field label={t('Recovery code')}>
            <Input
              autoFocus
              autoComplete="off"
              spellCheck={false}
              value={recovery}
              onChange={(e) => setRecovery(e.target.value)}
              placeholder="xxxx xxxx xxxx"
            />
          </Field>
        )}
        <Button type="submit" fullWidth loading={verify.isPending} disabled={!canSubmit}>
          {t('Verify')}
        </Button>
        <div className="flex justify-center text-meta">
          <button type="button" className="rounded-sm font-medium text-muted hover:text-text focus-ring" onClick={switchMode}>
            {mode === 'code' ? t('Use a recovery code instead') : t('Use my Authenticator app')}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
